import { useRef, useState } from 'react';
import { Box } from '@mui/material';
import { Icon, Loader, Typography } from '@sm-dashboard/ui-components';
import instanceAxios from '@/util/axios';
import { uploadFileUrl } from '@/constants';

interface AiAdImageDropzoneProps {
  disabled: boolean;
  imageUrl: string | null;
  onImageChange: (url: string | null) => void;
  onUploadingChange: (uploading: boolean) => void;
}

/** Drag & drop / click-to-upload zone for the event ad image. Uploads immediately and reports the public URL. */
export const AiAdImageDropzone = ({ disabled, imageUrl, onImageChange, onUploadingChange }: AiAdImageDropzoneProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const uploadFile = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      setError('ניתן להעלות קובץ תמונה בלבד');
      return;
    }
    setError(null);
    setIsUploading(true);
    onUploadingChange(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await instanceAxios.post(uploadFileUrl, formData);
      if (data?.publicUrl) {
        onImageChange(data.publicUrl);
      } else {
        setError('העלאת התמונה נכשלה, נסו שוב');
      }
    } catch {
      setError('העלאת התמונה נכשלה, נסו שוב');
    } finally {
      setIsUploading(false);
      onUploadingChange(false);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = ''; // allow re-selecting the same file
    if (file) uploadFile(file);
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(false);
    if (disabled || isUploading) return;
    const file = event.dataTransfer.files?.[0];
    if (file) uploadFile(file);
  };

  return (
    <Box
      onClick={() => !disabled && !isUploading && inputRef.current?.click()}
      onDragOver={event => {
        event.preventDefault();
        if (!disabled && !isUploading) setIsDragOver(true);
      }}
      onDragLeave={() => setIsDragOver(false)}
      onDrop={handleDrop}
      sx={{
        border: '1px dashed',
        borderColor: 'divider',
        borderRadius: '10px',
        p: 3,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1,
        cursor: disabled || isUploading ? 'default' : 'pointer',
        bgcolor: isDragOver ? 'action.hover' : 'transparent',
        textAlign: 'center',
        '&:hover': disabled || isUploading ? undefined : { bgcolor: 'action.hover' }
      }}
    >
      <input ref={inputRef} type="file" accept="image/*" hidden onChange={handleFileChange} />
      {isUploading ? (
        <>
          <Loader size={24} />
          <Typography variant="body">מעלה את התמונה...</Typography>
        </>
      ) : imageUrl ? (
        <>
          <Box
            component="img"
            src={imageUrl}
            alt="מודעת האירוע שהועלתה"
            sx={{ maxHeight: 120, maxWidth: '100%', borderRadius: '6px' }}
          />
          <Typography variant="body">לחצו או גררו תמונה אחרת להחלפה</Typography>
        </>
      ) : (
        <>
          <Icon icon="fileUploadOutlined" color="text.secondary" size="large" />
          <Box sx={{ color: 'text.secondary' }}>
            <Typography variant="body">גרור תמונה לכאן או לחץ להעלאה</Typography>
          </Box>
        </>
      )}
      {error && (
        <Box sx={{ color: 'error.main' }}>
          <Typography variant="body">{error}</Typography>
        </Box>
      )}
    </Box>
  );
};
